/**
 * GlobalPresentingOverlay
 *
 * A self-contained, app-wide overlay that appears whenever an organizer starts
 * presenting a session card. It works on every page in the app:
 *
 * - Polls event status every 3 s to detect presenting changes.
 * - When presenting starts (null → card): auto-expands the full overlay.
 * - When a new card is presented while minimized: prominently highlights the
 *   minimized bar (pulse + NEW badge) so the user knows.
 * - Supports voting (raise hand / skip) via InterestCard.
 * - Excluded from PresentPage (organizer has their own live panel).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { InterestCard, HandIcon } from './InterestCard';
import * as api from '../lib/api';
import { getAccessCode, getOrganizerToken } from '../lib/db';
import type { Event, Proposal } from '../types';

const POLL_MS = 3000;
const NEW_CARD_ALERT_DURATION = 5000;

export function GlobalPresentingOverlay() {
  const location = useLocation();

  // ----- Determine context -----

  // Don't render on PresentPage (organiser has their own live panel) or kiosk display
  const isPresentPage = location.pathname.includes('/present');
  const isKioskPage = location.pathname.includes('/kiosk');

  // Extract eventId from URL  (/event/:eventId/…)
  const eventIdFromUrl = useMemo(() => {
    const m = location.pathname.match(/^\/event\/([^/]+)/);
    return m?.[1] ?? null;
  }, [location.pathname]);

  // Fallback: fetch the active event when not on an event page
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  useEffect(() => {
    if (!eventIdFromUrl) {
      api.fetchActiveEvent().then((ev) => setActiveEventId(ev?.id ?? null));
    }
  }, [eventIdFromUrl]);

  const eventId = eventIdFromUrl || activeEventId;

  // ----- Access / auth -----

  const [hasAccess, setHasAccess] = useState(false);
  const [isOrganizer, setIsOrganizer] = useState(false);

  useEffect(() => {
    if (!eventId) {
      setHasAccess(false);
      setIsOrganizer(false);
      return;
    }
    const check = async () => {
      const [ac, ot] = await Promise.all([
        getAccessCode(eventId),
        getOrganizerToken(eventId),
      ]);
      setHasAccess(!!ac);
      setIsOrganizer(!!ot);
    };
    check();
    // Re-check periodically (user might log in on another page)
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [eventId]);

  // ----- Event status polling -----

  const [event, setEvent] = useState<Event | null>(null);

  useEffect(() => {
    if (!eventId) { setEvent(null); return; }
    let mounted = true;
    const poll = async () => {
      try {
        const ev = await api.fetchEvent(eventId);
        if (mounted) setEvent(ev);
      } catch {
        /* silently ignore */
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { mounted = false; clearInterval(id); };
  }, [eventId]);

  // ----- Proposal cache -----

  const proposalsRef = useRef<Proposal[]>([]);
  const [presentingProposal, setPresentingProposal] = useState<Proposal | null>(null);

  // Pre-fetch proposals list once when eventId is known
  useEffect(() => {
    if (!eventId) return;
    api.fetchProposals(eventId).then((p) => { proposalsRef.current = p; });
  }, [eventId]);

  // Resolve presenting proposal from cache (or re-fetch if missing)
  useEffect(() => {
    const pid = event?.presentingProposalId;
    if (!pid) { setPresentingProposal(null); return; }

    const cached = proposalsRef.current.find((p) => p.id === pid);
    if (cached) {
      setPresentingProposal(cached);
    } else if (eventId) {
      api.fetchProposals(eventId).then((props) => {
        proposalsRef.current = props;
        setPresentingProposal(props.find((p) => p.id === pid) ?? null);
      });
    }
  }, [event?.presentingProposalId, eventId]);

  // ----- Interest tracking -----

  const [interestedIds, setInterestedIds] = useState<Set<string>>(new Set());
  const [skippedIds, setSkippedIds] = useState<Set<string>>(new Set());
  const [interestSubmitting, setInterestSubmitting] = useState(false);

  // Fetch existing votes once per eventId
  useEffect(() => {
    if (!eventId) return;
    api.fetchMyVotes(eventId).then((votes) => {
      const ids = new Set<string>();
      votes.forEach((v) => {
        if (v.targetType === 'proposal' && v.weight > 0) ids.add(v.targetId);
      });
      setInterestedIds(ids);
    });
  }, [eventId]);

  const handleInterested = useCallback(async () => {
    if (!eventId || !presentingProposal) return;
    setInterestSubmitting(true);
    try {
      await api.vote(eventId, 'proposal', presentingProposal.id, 1);
      setInterestedIds((prev) => new Set(prev).add(presentingProposal.id));
    } catch {
      /* ignore */
    }
    setInterestSubmitting(false);
  }, [eventId, presentingProposal]);

  const handleSkip = useCallback(() => {
    if (!presentingProposal) return;
    setSkippedIds((prev) => new Set(prev).add(presentingProposal.id));
  }, [presentingProposal]);

  // ----- Overlay state -----

  const [minimized, setMinimized] = useState(false);
  const [newCardAlert, setNewCardAlert] = useState(false);
  const alertTimerRef = useRef<ReturnType<typeof setTimeout>>();

  /**
   * Track the previous presentingProposalId so we can detect transitions:
   *   'init'  → first render, no decision yet
   *   null    → nothing is being presented
   *   string  → a specific proposal is being presented
   */
  const prevPresentingId = useRef<string | null | 'init'>('init');

  useEffect(() => {
    const currentId = event?.presentingProposalId ?? null;

    if (prevPresentingId.current === 'init') {
      // First load – if something is already presenting, show the overlay
      prevPresentingId.current = currentId;
      if (currentId) setMinimized(false);
      return;
    }

    if (currentId && currentId !== prevPresentingId.current) {
      // A new card started presenting
      if (!prevPresentingId.current) {
        // Presenting just kicked off (null → card) → always auto-expand
        setMinimized(false);
      }
      // For *any* new card: fire the "new card" alert
      setNewCardAlert(true);
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = setTimeout(() => setNewCardAlert(false), NEW_CARD_ALERT_DURATION);
    }

    prevPresentingId.current = currentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.presentingProposalId]);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(alertTimerRef.current), []);

  // ----- Render logic -----

  // Don't render in specific cases
  if (isPresentPage || isKioskPage) return null;
  if (!presentingProposal) return null;
  if (!hasAccess && !isOrganizer) return null;

  // ===== Full-screen overlay (expanded) =====
  if (!minimized) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Minimize button */}
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={() => setMinimized(true)}
              className="text-white/70 hover:text-white text-sm flex items-center gap-1 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Minimize presenting overlay"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Minimize
            </button>
          </div>

          <h2 className="text-lg font-semibold text-white mb-3 text-center">
            Now Presenting
          </h2>

          {event?.votingOpen ? (
            interestedIds.has(presentingProposal.id) || skippedIds.has(presentingProposal.id) ? (
              <div className="text-center p-6 card">
                <div className="interest-done-badge mx-auto inline-flex">
                  {interestedIds.has(presentingProposal.id) ? (
                    <>
                      <HandIcon />
                      Hand raised for &quot;{presentingProposal.title}&quot;
                    </>
                  ) : (
                    <>Skipped &quot;{presentingProposal.title}&quot;</>
                  )}
                </div>
                <p className="text-sm text-gray-400 mt-2">Waiting for the next card…</p>
              </div>
            ) : (
              <InterestCard
                proposal={presentingProposal}
                alreadyInterested={false}
                onInterested={handleInterested}
                onSkip={handleSkip}
                submitting={interestSubmitting}
              />
            )
          ) : (
            <div className="text-center p-6 card">
              <p className="text-sm text-gray-500 mb-1">Currently presenting</p>
              <p className="text-lg font-semibold text-gray-900">
                {presentingProposal.displayId != null && (
                  <span className="text-primary-600 font-mono mr-1">#{presentingProposal.displayId}</span>
                )}
                {presentingProposal.title}
              </p>
              <p className="text-sm text-gray-500 mt-1">Raise hand is not open yet.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== Minimized bar =====
  return (
    <button
      type="button"
      onClick={() => { setMinimized(false); setNewCardAlert(false); }}
      className={`presenting-minimized-bar ${newCardAlert ? 'presenting-bar-alert' : ''}`}
      aria-label="Expand presenting overlay"
    >
      {newCardAlert && <span className="presenting-new-badge">NEW CARD</span>}

      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>

      <span className="truncate">
        Now presenting: {presentingProposal.title}
      </span>

      {interestedIds.has(presentingProposal.id) && (
        <span className="inline-flex items-center gap-0.5 flex-shrink-0">
          <HandIcon className="w-4 h-4" /> raised
        </span>
      )}
    </button>
  );
}
